import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import type { CandidateCreationTrendData, CandidatesByStatusData } from "@/hooks/dashboard/useDashboardKPIs";
import { Badge } from "@/components/ui/badge";

interface CandidateCreationChartProps {
	total: number;
	trend: CandidateCreationTrendData[];
	byStatus: CandidatesByStatusData[];
}

// Color palette for different statuses
const STATUS_COLORS: Record<string, string> = {
	"open": "#22c55e",
	"looking": "#3b82f6",
	"new": "#a855f7",
	"unavailable": "#ef4444",
	"hired": "#10b981",
	"Unknown": "#94a3b8",
};

export function CandidateCreationChart({ total, trend, byStatus }: CandidateCreationChartProps) {
	const { t } = useTranslation();

	const isEmpty = trend.length === 0;

	// Extract all unique statuses from the data
	const statuses = Array.from(
		new Set(
			trend.flatMap(item =>
				Object.keys(item).filter(key => key !== "period" && key !== "count")
			)
		)
	);

	const chartData = trend.map(d => ({
		...d,
		label: formatPeriod(d.period),
	}));

	// Assign colors to statuses
	const getStatusColor = (status: string, index: number): string => {
		if (STATUS_COLORS[status]) return STATUS_COLORS[status];
		// Fallback colors for unknown statuses
		const fallbackColors = ["#6366f1", "#ec4899", "#f59e0b", "#8b5cf6", "#14b8a6"];
		return fallbackColors[index % fallbackColors.length];
	};

	return (
		<Card>
			<CardHeader className={isEmpty ? "pb-2" : undefined}>
				<CardTitle className="text-lg">{t("dashboard.kpi.candidateCreationTrend")}</CardTitle>
			</CardHeader>
			<CardContent>
				{isEmpty ? (
					<div className="text-muted-foreground text-sm py-2">
						{t("dashboard.kpi.noData")}
					</div>
				) : (
					<>
						<div className="mb-4 flex flex-wrap items-center gap-3">
							<span className="text-sm text-muted-foreground">
								{t("dashboard.kpi.candidatesCreated")}: <span className="font-semibold text-foreground">{total}</span>
							</span>
							{byStatus.slice(0, 5).map(s => (
								<Badge key={s.status} variant="secondary" className="text-xs">
									{s.status}: {s.count}
								</Badge>
							))}
						</div>
						<div className="h-[350px]">
							<ResponsiveContainer width="100%" height="100%">
								<BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
									<CartesianGrid strokeDasharray="3 3" opacity={0.1} />
									<XAxis dataKey="label" tick={{ fontSize: 11 }} />
									<YAxis allowDecimals={false} />
									<Tooltip
										contentStyle={{
											backgroundColor: "rgba(255, 255, 255, 0.95)",
											borderRadius: "0.5rem",
											border: "none",
											boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
										}}
									/>
									<Legend wrapperStyle={{ fontSize: "12px" }} />
									{statuses.map((status, index) => (
										<Bar
											key={status}
											dataKey={status}
											stackId="status"
											fill={getStatusColor(status, index)}
											name={status}
										/>
									))}
								</BarChart>
							</ResponsiveContainer>
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
}

function formatPeriod(period: string): string {
	const [year, month] = period.split("-");
	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	return `${months[parseInt(month, 10) - 1]} ${year}`;
}
